import { useEffect, useState } from "react";
import { Link } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { MoreHorizontal, Plus, Sparkles, Building2, Globe, BookOpen, FolderOpen, Video, Newspaper, BadgeCheck } from "lucide-react";
import { BrandAppearanceFields, type BrandAppearanceValue } from "@/components/shell/BrandAppearanceFields";
import { BrandIcon } from "@/components/shell/BrandIcon";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, api } from "@/lib/api";
import { useAccountWorkspace, type WorkspaceBrand } from "@/lib/account-workspace";
import { useAuth } from "@/lib/auth";

const brandSchema = z.object({
  name: z.string().trim().min(1, "请输入品牌名称").max(100, "品牌名称不能超过 100 个字符"),
});

type BrandFormValues = z.infer<typeof brandSchema>;
type Membership = { user_id: number; role: "owner" | "admin" | "member" };
type DiscardedBrand = WorkspaceBrand & {
  discarded_at: string;
  discard_expires_at?: string | null;
};

type DemoChannel = {
  key: string;
  name: string;
  lit: boolean;
  icon: typeof Globe;
};

const DEMO_CHANNELS: DemoChannel[] = [
  { key: "website", name: "官网", lit: true, icon: Globe },
  { key: "wechat_mp", name: "公众号", lit: true, icon: Newspaper },
  { key: "baike", name: "百度百科", lit: false, icon: BookOpen },
  { key: "baijiahao", name: "百家号", lit: false, icon: Newspaper },
  { key: "douyin", name: "抖音号", lit: true, icon: Video },
  { key: "xiaohongshu", name: "小红书", lit: false, icon: Sparkles },
];

function formatExpiry(iso: string | null | undefined) {
  if (!iso) return "30 天内";
  try {
    return new Date(iso).toLocaleDateString("zh-CN", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "30 天内";
  }
}

/** UX Demo：企业 Profile + 品牌 CRUD / 回收站 */
export default function EnterprisePage() {
  const { account, brands, accountId, accessKind, refreshBrands } = useAccountWorkspace();
  const { user } = useAuth();
  const [canManage, setCanManage] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<WorkspaceBrand | null>(null);
  const [deletingBrand, setDeletingBrand] = useState<WorkspaceBrand | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [restoringBrand, setRestoringBrand] = useState<DiscardedBrand | null>(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [discarded, setDiscarded] = useState<DiscardedBrand[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [channels, setChannels] = useState(DEMO_CHANNELS);

  const litCount = channels.filter((item) => item.lit).length;
  const verified = Boolean(account?.enterprise_verified_at);

  const refreshDiscarded = async () => {
    if (!accountId || accessKind !== "membership") {
      setDiscarded([]);
      return;
    }
    try {
      const { brands: trash } = await api.fetchForAccount<{ brands: DiscardedBrand[] }>(
        accountId,
        `/accounts/${accountId}/brands/discarded`,
      );
      setDiscarded(trash);
    } catch {
      setDiscarded([]);
    }
  };

  useEffect(() => {
    if (!accountId || !user?.id || accessKind !== "membership") {
      setCanManage(false);
      setDiscarded([]);
      return;
    }
    api
      .fetchForAccount<{ memberships: Membership[] }>(accountId, `/accounts/${accountId}/memberships`)
      .then(({ memberships }) => {
        const membership = memberships.find((item) => item.user_id === user.id);
        const manager = membership?.role === "owner" || membership?.role === "admin";
        setCanManage(manager);
        if (manager) void refreshDiscarded();
      })
      .catch(() => {
        setCanManage(false);
        setDiscarded([]);
      });
  }, [accountId, user?.id, accessKind]);

  async function createBrand(values: {
    name: string;
    icon_key: string | null;
    color: string | null;
  }) {
    const { brand } = await api.fetchForAccount<{ brand: WorkspaceBrand }>(
      accountId,
      `/accounts/${accountId}/brands`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      },
    );
    await refreshBrands();
    return brand;
  }

  async function updateBrand(
    brandId: number,
    values: { name: string; icon_key: string | null; color: string | null },
  ) {
    const { brand } = await api.fetchForAccount<{ brand: WorkspaceBrand }>(
      accountId,
      `/accounts/${accountId}/brands/${brandId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      },
    );
    await refreshBrands();
    return brand;
  }

  async function confirmDeleteBrand() {
    if (!deletingBrand) return;
    setError(null);
    setDeleting(true);
    try {
      await api.fetchForAccount(accountId, `/accounts/${accountId}/brands/${deletingBrand.id}`, {
        method: "DELETE",
      });
      setDeletingBrand(null);
      setDeleteConfirmName("");
      await refreshBrands();
      await refreshDiscarded();
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "删除品牌失败");
    } finally {
      setDeleting(false);
    }
  }

  async function restoreBrand(brand: DiscardedBrand) {
    setError(null);
    setRestoringId(brand.id);
    try {
      await api.fetchForAccount(accountId, `/accounts/${accountId}/brands/${brand.id}/restore`, {
        method: "POST",
      });
      setRestoringBrand(null);
      await refreshBrands();
      if (!accountId || accessKind !== "membership") {
        setDiscarded([]);
        setTrashOpen(false);
        return;
      }
      const { brands: trash } = await api.fetchForAccount<{ brands: DiscardedBrand[] }>(
        accountId,
        `/accounts/${accountId}/brands/discarded`,
      );
      setDiscarded(trash);
      if (trash.length === 0) setTrashOpen(false);
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "恢复品牌失败");
    } finally {
      setRestoringId(null);
    }
  }

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="border-b border-border bg-gradient-to-br from-muted/50 to-transparent px-6 py-6 md:px-8 md:py-8">
          <div className="page-eyebrow">UX Demo · 企业 Profile</div>
          <div className="mt-4 flex flex-wrap items-start gap-4">
            <div className="flex size-16 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Building2 className="size-7" />
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-tight">{account?.name ?? "企业"}</h1>
                {verified ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                    <BadgeCheck className="size-3.5" />
                    已企业认证
                  </span>
                ) : (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">未企业认证</span>
                )}
              </div>
              <p className="max-w-2xl text-sm text-muted-foreground">
                对外呈现的企业形象与触点总览。知识库在后台支撑话术与事实，用户感知更像公司主页。
              </p>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span>账户 #{account?.id}</span>
                <span>·</span>
                <span>
                  对外渠道已点亮 {litCount}/{channels.length}
                </span>
                <span>·</span>
                <span>品牌 {brands.length}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 md:px-8">
          <div className="mb-3">
            <h2 className="font-medium">对外渠道</h2>
            <p className="text-sm text-muted-foreground">已接入则点亮；Demo 可点击切换状态，正式数据后续接入。</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {channels.map((channel) => (
              <button
                key={channel.key}
                type="button"
                onClick={() =>
                  setChannels((current) =>
                    current.map((item) => (item.key === channel.key ? { ...item, lit: !item.lit } : item)),
                  )
                }
                className={cn(
                  "flex flex-col items-center gap-2 rounded-lg border px-3 py-4 text-center transition",
                  channel.lit
                    ? "border-primary/40 bg-primary/10 text-foreground"
                    : "border-border bg-muted/20 text-muted-foreground hover:border-foreground/20",
                )}
              >
                <channel.icon className={cn("size-5", channel.lit ? "text-primary" : "opacity-50")} />
                <span className="text-xs font-medium">{channel.name}</span>
                <span className="text-[10px] opacity-70">{channel.lit ? "已点亮" : "未接入"}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-medium">品牌</h2>
          {canManage && discarded.length > 0 && (
            <Button
              type="button"
              variant="link"
              className="h-auto px-0 text-sm text-muted-foreground"
              onClick={() => {
                void refreshDiscarded();
                setTrashOpen(true);
              }}
            >
              回收站（{discarded.length}）
            </Button>
          )}
        </div>
        {error && (
          <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {brands.map((brand) => (
            <div
              key={brand.id}
              className="group relative rounded-lg border border-border bg-card p-5 transition hover:border-foreground/20"
            >
              <Link to={`/accounts/${accountId}/brands/${brand.id}`} className="block pr-8">
                <div className="mb-3">
                  <BrandIcon
                    name={brand.name}
                    iconKey={brand.icon_key}
                    color={brand.color}
                    size="lg"
                  />
                </div>
                <div className="font-heading font-medium">{brand.name}</div>
                <p className="mt-1 text-sm text-muted-foreground">进入品牌知识库与子应用</p>
              </Link>
              {canManage && (
                <div className="absolute top-3 right-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="ghost" size="icon" className="size-8" aria-label={`${brand.name} 操作`}>
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => setEditingBrand(brand)}>编辑</DropdownMenuItem>
                      <DropdownMenuItem variant="destructive" onSelect={() => setDeletingBrand(brand)}>
                        删除
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          ))}

          {canManage && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="flex flex-col items-start justify-center rounded-lg border border-dashed border-border bg-card/40 p-5 text-left text-muted-foreground transition hover:border-foreground/25 hover:text-foreground"
            >
              <div className="mb-3 flex size-9 items-center justify-center rounded-lg border border-dashed border-border">
                <Plus className="size-4" />
              </div>
              <div className="font-medium">添加品牌</div>
              <p className="mt-1 text-sm">为账户创建新的品牌实体</p>
            </button>
          )}

          {brands.length === 0 && !canManage && (
            <div className="rounded-lg border border-dashed border-border px-5 py-10 text-center text-sm text-muted-foreground sm:col-span-2 lg:col-span-3">
              暂无品牌
            </div>
          )}
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="font-medium">企业内容</h2>
          <p className="text-sm text-muted-foreground">
            企业素材库与知识库在侧栏与企业画像同级；品牌内容请进入对应品牌面板。
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Link
            to={`/accounts/${accountId}/materials`}
            className="rounded-lg border border-border bg-card p-5 transition hover:border-foreground/25"
          >
            <FolderOpen className="size-5 text-primary" />
            <div className="mt-3 font-medium">素材库</div>
            <p className="mt-1 text-sm text-muted-foreground">企业简介等成件（即将开放）</p>
          </Link>
          <Link
            to={`/accounts/${accountId}/kb`}
            className="rounded-lg border border-border bg-card p-5 transition hover:border-foreground/25"
          >
            <BookOpen className="size-5 text-primary" />
            <div className="mt-3 font-medium">企业知识库</div>
            <p className="mt-1 text-sm text-muted-foreground">企业主体独立知识要点</p>
          </Link>
        </div>
      </section>

      <BrandFormDialog
        open={createOpen}
        title="添加品牌"
        description="创建后会出现在侧栏与本页品牌列表中。"
        submitLabel="创建"
        onOpenChange={setCreateOpen}
        onSubmit={async (values) => {
          await createBrand(values);
          setCreateOpen(false);
        }}
      />

      <BrandFormDialog
        open={Boolean(editingBrand)}
        title="编辑品牌"
        description="修改品牌名称、图标与颜色。"
        submitLabel="保存"
        defaultName={editingBrand?.name ?? ""}
        defaultIconKey={editingBrand?.icon_key ?? null}
        defaultColor={editingBrand?.color ?? null}
        onOpenChange={(open) => {
          if (!open) setEditingBrand(null);
        }}
        onSubmit={async (values) => {
          if (!editingBrand) return;
          await updateBrand(editingBrand.id, values);
          setEditingBrand(null);
        }}
      />

      <Dialog
        open={Boolean(deletingBrand)}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingBrand(null);
            setDeleteConfirmName("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>删除品牌「{deletingBrand?.name}」？</DialogTitle>
            <DialogDescription>
              品牌将进入回收站，保留 30 天，期间可随时恢复；到期后系统会自动彻底清除，不可再恢复。请输入品牌名称以确认。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="delete-brand-confirm-name">
              请输入 <span className="font-medium text-foreground">{deletingBrand?.name}</span> 以确认
            </Label>
            <Input
              id="delete-brand-confirm-name"
              value={deleteConfirmName}
              onChange={(event) => setDeleteConfirmName(event.target.value)}
              placeholder={deletingBrand?.name}
              autoComplete="off"
              disabled={deleting}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeletingBrand(null);
                setDeleteConfirmName("");
              }}
              disabled={deleting}
            >
              取消
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void confirmDeleteBrand()}
              disabled={deleting || deleteConfirmName.trim() !== deletingBrand?.name}
            >
              {deleting ? "删除中…" : "移入回收站"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={trashOpen} onOpenChange={setTrashOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>品牌回收站</DialogTitle>
            <DialogDescription>
              已删除品牌保留 30 天，到期自动彻底清除；期间可恢复。
            </DialogDescription>
          </DialogHeader>
          {discarded.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">回收站为空</p>
          ) : (
            <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-lg border border-border">
              {discarded.map((brand) => (
                <li key={brand.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div>
                    <div className="font-heading font-medium">{brand.name}</div>
                    <div className="text-xs text-muted-foreground">
                      将于 {formatExpiry(brand.discard_expires_at)} 前可恢复
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={restoringId === brand.id}
                    onClick={() => setRestoringBrand(brand)}
                  >
                    恢复
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTrashOpen(false)}>
              关闭
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(restoringBrand)}
        onOpenChange={(open) => {
          if (!open && restoringId === null) setRestoringBrand(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>恢复品牌「{restoringBrand?.name}」？</DialogTitle>
            <DialogDescription>
              恢复后品牌将重新出现在侧栏与品牌列表中，可正常进入品牌工作台。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRestoringBrand(null)}
              disabled={restoringId !== null}
            >
              取消
            </Button>
            <Button
              type="button"
              disabled={!restoringBrand || restoringId !== null}
              onClick={() => restoringBrand && void restoreBrand(restoringBrand)}
            >
              {restoringId !== null ? "恢复中…" : "确认恢复"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BrandFormDialog({
  open,
  title,
  description,
  submitLabel,
  defaultName = "",
  defaultIconKey = null,
  defaultColor = null,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  title: string;
  description: string;
  submitLabel: string;
  defaultName?: string;
  defaultIconKey?: string | null;
  defaultColor?: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: {
    name: string;
    icon_key: string | null;
    color: string | null;
  }) => Promise<void>;
}) {
  const [appearance, setAppearance] = useState<BrandAppearanceValue>({
    iconKey: defaultIconKey,
    color: defaultColor,
  });
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<BrandFormValues>({
    resolver: zodResolver(brandSchema),
    defaultValues: { name: defaultName },
  });
  const nameValue = watch("name");

  useEffect(() => {
    if (open) {
      reset({ name: defaultName });
      setAppearance({ iconKey: defaultIconKey, color: defaultColor });
    }
  }, [open, defaultName, defaultIconKey, defaultColor, reset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={handleSubmit(async ({ name }) => {
            try {
              await onSubmit({
                name,
                icon_key: appearance.iconKey,
                color: appearance.color,
              });
            } catch (caughtError) {
              setError("root", {
                message: caughtError instanceof ApiError ? caughtError.message : "操作失败，请稍后重试",
              });
            }
          })}
          noValidate
        >
          <div className="space-y-2">
            <Label htmlFor="brand-dialog-name">品牌名称</Label>
            <Input id="brand-dialog-name" {...register("name")} aria-invalid={Boolean(errors.name)} />
            {errors.name && (
              <p className="text-sm text-destructive" role="alert">
                {errors.name.message}
              </p>
            )}
          </div>
          <BrandAppearanceFields
            name={nameValue}
            value={appearance}
            onChange={setAppearance}
            modal
            idPrefix="enterprise-brand"
          />
          {errors.root && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
              {errors.root.message}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "提交中…" : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
